import type { Account } from "@shared/schema";

// The account page's Notifications tab. An account's own notification settings are
// fourteen nullable columns, seven a card; null means "follow the org's setting"
// (server/services/notificationResolver.ts). The email address is not one of them:
// it has no org default, and "Reset to org defaults" leaves it alone.

export const SMS_OVERRIDE_FIELDS = [
  "reminderEnabled",
  "customReminderMessage",
  "firstReminderDay",
  "firstReminderTime",
  "secondReminderDay",
  "secondReminderTime",
  "smsOrderReceipts",
] as const;

export const EMAIL_OVERRIDE_FIELDS = [
  "emailNotificationEnabled",
  "customEmailMessage",
  "firstEmailDay",
  "firstEmailTime",
  "secondEmailDay",
  "secondEmailTime",
  "emailOrderReceipts",
] as const;

type OverrideField = (typeof SMS_OVERRIDE_FIELDS)[number] | (typeof EMAIL_OVERRIDE_FIELDS)[number];

/**
 * A card follows the org when the account has no setting of its own on it — every
 * field, not only the on/off switch: a customer who verifies an email address to get
 * order summaries has `emailOrderReceipts` and nothing else.
 */
export function followsOrgDefaults(account: Pick<Account, OverrideField>, fields: readonly OverrideField[]): boolean {
  return fields.every((field) => account[field] === null);
}

/** What "Reset to org defaults" sends for a card: every field null. */
export function resetBody<F extends OverrideField>(fields: readonly F[]): Record<F, null> {
  return Object.fromEntries(fields.map((field) => [field, null])) as Record<F, null>;
}
